/**
 * Admin API Service
 *
 * Provides API methods for admin tenant management.
 * Uses Supabase auth token for authentication.
 *
 * @fileoverview Admin API service for tenant CRUD operations
 */

import { supabase } from '../lib/supabase';
import { apiFetch, apiJson } from '../lib/api';
import { buildApiError } from '../lib/apiError';
import {
  AdminTenant,
  TenantQueryParams,
  PaginatedResponse,
  TenantStats,
  CreateTenantInput,
  UpdateTenantInput,
  AdminInvite,
  InviteQueryParams,
  InviteStats,
  CreateInviteInput,
  UpdateInviteInput,
  TokenValidationResult,
  PlaidTokenInfo,
  PlaidConnectionTestResult,
  PlaidConnectionInfo,
  PlaidHealthBoardResponse,
  JobsStatusResponse,
  AdminUser,
  UserQueryParams,
  UserStats,
  UpdateAdminUserInput,
  UpdateMembershipInput,
  AddMembershipInput,
  CreateAdminUserInviteInput,
  AccountsOverviewResponse,
} from '../types/admin';

const ADMIN_API_BASE = '/api/admin';

/**
 * Get the current auth token from Supabase session
 */
async function getAuthToken(): Promise<string> {
  const { data: { session } } = await supabase.auth.getSession();
  if (!session?.access_token) {
    throw new Error('Not authenticated');
  }
  return session.access_token;
}

/**
 * Make an authenticated API request
 */
async function apiRequest<T>(
  endpoint: string,
  options: RequestInit = {}
): Promise<T> {
  const token = await getAuthToken();

  const response = await apiFetch(`${ADMIN_API_BASE}${endpoint}`, {
    ...options,
    headers: {
      'Content-Type': 'application/json',
      ...options.headers,
    },
  }, token);

  // Same normalization as apiJson: a readable message on `message`, the
  // status and raw body on the ApiError for logs. This used to fall back to
  // "HTTP error 500", which told an admin nothing.
  if (!response.ok) {
    throw buildApiError(response.status, await response.text());
  }

  // Handle 204 No Content (successful DELETE, etc.)
  if (response.status === 204) {
    return undefined as T;
  }

  return response.json();
}

/**
 * Build query string from params object
 */
function buildQueryString(params: Record<string, unknown>): string {
  const searchParams = new URLSearchParams();

  Object.entries(params).forEach(([key, value]) => {
    if (value !== undefined && value !== null && value !== '') {
      searchParams.append(key, String(value));
    }
  });

  const queryString = searchParams.toString();
  return queryString ? `?${queryString}` : '';
}

/**
 * Admin API methods for tenant management
 */
export const adminApi = {
  tenants: {
    /**
     * List all tenants with pagination and filtering
     */
    list: async (params: TenantQueryParams = {}): Promise<PaginatedResponse<AdminTenant>> => {
      const queryString = buildQueryString(params as Record<string, unknown>);
      return apiRequest<PaginatedResponse<AdminTenant>>(`/tenants${queryString}`);
    },

    /**
     * Get dashboard statistics
     */
    getStats: async (): Promise<TenantStats> => {
      return apiRequest<TenantStats>('/tenants/stats');
    },

    /**
     * Get recent tenants
     */
    getRecent: async (limit: number = 5): Promise<AdminTenant[]> => {
      return apiRequest<AdminTenant[]>(`/tenants/recent?limit=${limit}`);
    },

    /**
     * Get a single tenant by ID
     */
    getById: async (id: string): Promise<AdminTenant> => {
      return apiRequest<AdminTenant>(`/tenants/${id}`);
    },

    /**
     * Create a new tenant
     */
    create: async (data: CreateTenantInput): Promise<AdminTenant> => {
      return apiRequest<AdminTenant>('/tenants', {
        method: 'POST',
        body: JSON.stringify(data),
      });
    },

    /**
     * Update an existing tenant
     */
    update: async (id: string, data: UpdateTenantInput): Promise<AdminTenant> => {
      return apiRequest<AdminTenant>(`/tenants/${id}`, {
        method: 'PUT',
        body: JSON.stringify(data),
      });
    },

    /**
     * Delete a tenant
     */
    delete: async (id: string): Promise<AdminTenant> => {
      return apiRequest<AdminTenant>(`/tenants/${id}`, {
        method: 'DELETE',
      });
    },

    /**
     * Activate a tenant
     */
    activate: async (id: string): Promise<AdminTenant> => {
      return apiRequest<AdminTenant>(`/tenants/${id}/activate`, {
        method: 'POST',
      });
    },

    /**
     * Deactivate a tenant
     */
    deactivate: async (id: string): Promise<AdminTenant> => {
      return apiRequest<AdminTenant>(`/tenants/${id}/deactivate`, {
        method: 'POST',
      });
    },

    // ========================================
    // PLAID OPERATIONS
    // ========================================

    /**
     * Get Plaid status for a tenant
     */
    getPlaidStatus: async (id: string): Promise<PlaidTokenInfo | null> => {
      return apiRequest<PlaidTokenInfo | null>(`/tenants/${id}/plaid/status`);
    },

    /**
     * Test Plaid connection for a tenant
     */
    testPlaidConnection: async (id: string): Promise<PlaidConnectionTestResult> => {
      return apiRequest<PlaidConnectionTestResult>(`/tenants/${id}/plaid/test`, {
        method: 'POST',
      });
    },

    /**
     * Disconnect Plaid for a tenant
     */
    disconnectPlaid: async (id: string): Promise<void> => {
      const token = await getAuthToken();
      const response = await apiFetch(`${ADMIN_API_BASE}/tenants/${id}/plaid`, {
        method: 'DELETE',
      }, token);
      if (!response.ok) {
        throw buildApiError(response.status, await response.text());
      }
      // 204 No Content response
    },
    /**
     * Send reconnection email to tenant owner
     */
    sendReconnectEmail: async (id: string): Promise<{ success: boolean; message: string }> => {
      // Endpoint: /api/admin/tenants/:id/plaid/send-reconnect-email
      return apiRequest<{ success: boolean; message: string }>(`/tenants/${id}/plaid/send-reconnect-email`, {
        method: 'POST',
      });
    },

    // ── Danger Zone ───────────────────────────────────────────────────

    /**
     * List all Plaid connections for a tenant (no secrets returned)
     */
    getPlaidConnections: async (tenantId: string): Promise<PlaidConnectionInfo[]> => {
      return apiRequest<PlaidConnectionInfo[]>(`/tenants/${tenantId}/plaid-connections`);
    },

    /**
     * Admin removal of a specific Plaid connection.
     * Calls Plaid itemRemove server-side; only cleans up DB after Plaid confirms.
     */
    removePlaidConnection: async (
      tenantId: string,
      connectionId: string,
    ): Promise<{ plaidRequestId: string }> => {
      return apiRequest<{ plaidRequestId: string }>(
        `/tenants/${tenantId}/plaid-connections/${connectionId}`,
        { method: 'DELETE' },
      );
    },
  },

  invites: {
    /**
     * List all invites with pagination and filtering
     */
    list: async (params: InviteQueryParams = {}): Promise<PaginatedResponse<AdminInvite>> => {
      const queryString = buildQueryString(params as Record<string, unknown>);
      return apiRequest<PaginatedResponse<AdminInvite>>(`/invites${queryString}`);
    },

    /**
     * Get dashboard statistics
     */
    getStats: async (): Promise<InviteStats> => {
      return apiRequest<InviteStats>('/invites/stats');
    },

    /**
     * Get recent invites
     */
    getRecent: async (limit: number = 5): Promise<AdminInvite[]> => {
      return apiRequest<AdminInvite[]>(`/invites/recent?limit=${limit}`);
    },

    /**
     * Get a single invite by ID
     */
    getById: async (id: string): Promise<AdminInvite> => {
      return apiRequest<AdminInvite>(`/invites/${id}`);
    },

    /**
     * Send a standalone invite (user without tenant assignment)
     */
    sendInvite: async (data: CreateInviteInput): Promise<AdminInvite> => {
      return apiRequest<AdminInvite>('/invites/send', {
        method: 'POST',
        body: JSON.stringify(data),
      });
    },

    /**
     * Invite a user to join an existing tenant
     */
    inviteToTenant: async (data: { email: string; tenantId: string; tenantRole?: 'USER' | 'TENANT_ADMIN'; expiresAt?: string }): Promise<AdminInvite> => {
      return apiRequest<AdminInvite>('/invites/to-tenant', {
        method: 'POST',
        body: JSON.stringify(data),
      });
    },

    /**
     * Update an existing invite (revoke)
     */
    update: async (id: string, data: UpdateInviteInput): Promise<AdminInvite> => {
      return apiRequest<AdminInvite>(`/invites/${id}`, {
        method: 'PATCH',
        body: JSON.stringify(data),
      });
    },

    /**
     * Delete an invite
     */
    delete: async (id: string): Promise<void> => {
      await apiRequest<void>(`/invites/${id}`, {
        method: 'DELETE',
      });
    },

    /**
     * Resend an invite (regenerate token and send new email)
     */
    resend: async (id: string): Promise<AdminInvite> => {
      return apiRequest<AdminInvite>(`/invites/${id}/resend`, {
        method: 'POST',
      });
    },
  },

  plaid: {
    /**
     * Get the global Plaid health board
     * Returns all tenants with LOGIN_REQUIRED or UNKNOWN tokens
     */
    getHealthBoard: async (): Promise<PlaidHealthBoardResponse> => {
      return apiRequest<PlaidHealthBoardResponse>('/plaid/health');
    },
  },

  accounts: {
    /**
     * Get connected accounts overview for all tenants
     * Returns each tenant with their banks and accounts nested inside
     */
    getOverview: async (): Promise<AccountsOverviewResponse> => {
      return apiRequest<AccountsOverviewResponse>('/tenants/accounts-overview');
    },
  },

  jobs: {
    /**
     * Get scheduler job status (ops board)
     * Returns last run info and recent errors for each background job
     */
    getStatus: async (): Promise<JobsStatusResponse> => {
      return apiRequest<JobsStatusResponse>('/jobs/status');
    },
  },

  ai: {
    getConfig: async (): Promise<{
      defaultModel: string;
      agents: { id: string; label: string; systemPrompt: string; defaultPrompt: string }[];
      temperature: number;
      maxTokens: number;
      dailyTokenCapPerTenant: number;
      merchantNormalizationModel: string;
      merchantNormalizationPrompt: string;
      updatedAt: string;
    }> => {
      return apiRequest('/ai/config');
    },

    getModels: async (): Promise<string[]> => {
      const token = await getAuthToken();
      const response = await apiFetch('/api/ai/models', {}, token);
      if (!response.ok) return [];
      const data = await response.json();
      return data.models ?? [];
    },

    updateConfig: async (data: {
      defaultModel?: string;
      temperature?: number;
      maxTokens?: number;
      dailyTokenCapPerTenant?: number;
      merchantNormalizationModel?: string;
      merchantNormalizationPrompt?: string;
    }): Promise<void> => {
      await apiRequest('/ai/config', { method: 'PUT', body: JSON.stringify(data) });
    },

    getMerchantNormalizationStatus: async (
      tenantId: string,
    ): Promise<{
      pending: number;
      processing: number;
      normalized: number;
      nonMerchant: number;
      noMerchantMatch: number;
      failed: number;
      skipped: number;
      total: number;
      progressPct: number;
    }> => {
      const token = await getAuthToken();
      return apiJson(
        "/api/plaid-transactions/normalize-merchants/status",
        {
          method: "GET",
          headers: { "x-tenant-id": tenantId },
        },
        token,
      );
    },

    runMerchantNormalization: async (
      tenantId: string,
      data: {
        requeueFailed?: boolean;
        batchSize?: number;
        maxBatches?: number;
      } = {},
    ): Promise<{ accepted: boolean; queued: number; message: string }> => {
      const token = await getAuthToken();
      return apiJson<{ accepted: boolean; queued: number; message: string }>(
        "/api/plaid-transactions/normalize-merchants",
        {
          method: "POST",
          headers: {
            "x-tenant-id": tenantId,
          },
          body: JSON.stringify(data),
        },
        token,
      );
    },

    updateAgentPrompt: async (agentId: string, data: { defaultPrompt: string; systemPrompt?: string }): Promise<void> => {
      await apiRequest(`/ai/agents/${agentId}/prompt`, { method: 'PUT', body: JSON.stringify(data) });
    },

    createAgent: async (data: { id: string; label: string; systemPrompt: string; defaultPrompt: string }): Promise<void> => {
      await apiRequest('/ai/agents', { method: 'POST', body: JSON.stringify(data) });
    },

    deleteAgent: async (agentId: string): Promise<void> => {
      await apiRequest(`/ai/agents/${agentId}`, { method: 'DELETE' });
    },

    getUsage: async (tenantId: string): Promise<{
      tokensUsedToday: number;
      requestsToday: number;
      estimatedCostToday: number;
    }> => {
      return apiRequest(`/ai/usage?tenantId=${encodeURIComponent(tenantId)}`);
    },

    listConversations: async (tenantId: string, page = 1, pageSize = 20): Promise<{
      conversations: {
        id: string;
        title: string;
        model: string;
        agentId: string;
        groupBy: string;
        createdAt: string;
        _count: { messages: number };
        usageLogs: { inputTokens: number; outputTokens: number; costEstimate: number }[];
      }[];
      total: number;
      page: number;
      pageSize: number;
    }> => {
      return apiRequest(`/ai/conversations?tenantId=${encodeURIComponent(tenantId)}&page=${page}&pageSize=${pageSize}`);
    },

    getConversation: async (id: string, tenantId: string): Promise<{
      id: string;
      title: string;
      model: string;
      agentId: string;
      groupBy: string;
      createdAt: string;
      messages: { id: string; role: string; content: string; inputTokens?: number; outputTokens?: number; createdAt: string }[];
      usageLogs: { inputTokens: number; outputTokens: number; costEstimate: number; durationMs: number }[];
    } | null> => {
      return apiRequest(`/ai/conversations/${id}?tenantId=${encodeURIComponent(tenantId)}`);
    },
  },

  users: {
    /**
     * List all users with pagination and filtering
     */
    list: async (params: UserQueryParams = {}): Promise<PaginatedResponse<AdminUser>> => {
      const queryString = buildQueryString(params as Record<string, unknown>);
      return apiRequest<PaginatedResponse<AdminUser>>(`/users${queryString}`);
    },

    /**
     * Get user statistics
     */
    getStats: async (): Promise<UserStats> => {
      return apiRequest<UserStats>('/users/stats');
    },

    /**
     * Get recent users
     */
    getRecent: async (limit: number = 5): Promise<AdminUser[]> => {
      return apiRequest<AdminUser[]>(`/users/recent?limit=${limit}`);
    },

    /**
     * Get a single user by ID
     */
    getById: async (id: string): Promise<AdminUser> => {
      return apiRequest<AdminUser>(`/users/${id}`);
    },

    /**
     * Update a user
     */
    update: async (id: string, data: UpdateAdminUserInput): Promise<AdminUser> => {
      return apiRequest<AdminUser>(`/users/${id}`, {
        method: 'PATCH',
        body: JSON.stringify(data),
      });
    },

    /**
     * Update a user's tenant membership
     */
    updateMembership: async (userId: string, tenantId: string, data: UpdateMembershipInput): Promise<AdminUser> => {
      return apiRequest<AdminUser>(`/users/${userId}/memberships/${tenantId}`, {
        method: 'PATCH',
        body: JSON.stringify(data),
      });
    },

    /**
     * Add a user to a tenant
     */
    addMembership: async (userId: string, data: AddMembershipInput): Promise<AdminUser> => {
      return apiRequest<AdminUser>(`/users/${userId}/memberships`, {
        method: 'POST',
        body: JSON.stringify(data),
      });
    },

    /**
     * Remove a user from a tenant
     */
    removeMembership: async (userId: string, tenantId: string): Promise<AdminUser> => {
      return apiRequest<AdminUser>(`/users/${userId}/memberships/${tenantId}`, {
        method: 'DELETE',
      });
    },

    /**
     * Activate a user
     */
    activate: async (id: string): Promise<AdminUser> => {
      return apiRequest<AdminUser>(`/users/${id}/activate`, {
        method: 'POST',
      });
    },

    /**
     * Deactivate a user
     */
    deactivate: async (id: string): Promise<AdminUser> => {
      return apiRequest<AdminUser>(`/users/${id}/deactivate`, {
        method: 'POST',
      });
    },

    /**
     * Delete a user
     */
    delete: async (id: string): Promise<void> => {
      await apiRequest<void>(`/users/${id}`, {
        method: 'DELETE',
      });
    },

    /**
     * Send password reset email
     */
    sendPasswordReset: async (id: string): Promise<{ message: string }> => {
      return apiRequest<{ message: string }>(`/users/${id}/password-reset`, {
        method: 'POST',
      });
    },

    /**
     * Invite a new user to a tenant
     */
    invite: async (data: CreateAdminUserInviteInput): Promise<AdminInvite> => {
      return apiRequest<AdminInvite>('/users/invite', {
        method: 'POST',
        body: JSON.stringify(data),
      });
    },
  },
};

/**
 * Public API methods for invite validation (no auth required)
 */
export const publicApi = {
  invites: {
    /**
     * Validate an invite token
     */
    validateToken: async (token: string): Promise<TokenValidationResult> => {
      const response = await apiFetch(`/api/invites/validate/${token}`);
      if (!response.ok) {
        return { valid: false, reason: 'Invalid token' };
      }
      return response.json();
    },

    /**
     * Mark an invite as used after successful signup
     */
    markAsUsed: async (token: string): Promise<void> => {
      await apiFetch(`/api/invites/mark-used/${token}`, {
        method: 'POST',
      });
    },
  },
};

export default adminApi;
